import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { useAuth } from '../services/authProvider'
import { getDayNameTHByIndex, formatTimeShort } from '../utils/teachingUtils'

const fmtDateTH = (d) =>
  d ? new Date(d).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'

const fmtDays = (n) => {
  const v = parseFloat(n)
  if (Number.isNaN(v)) return '-'
  return Number.isInteger(v) ? String(v) : v.toFixed(1)
}

const todayLocal = () => new Date().toLocaleDateString('en-CA')

// ต้องตรงกับ requiredPositions ของ /academic/substitute-overview ใน ProtectedRoutes
const SUBSTITUTE_OVERVIEW_POSITIONS = [3, 4, 5, 99]

// สี Badge ตามประเภทการไม่อยู่
const TYPE_VARIANT = { leave: 'info', offsite: 'warning', training: 'primary' }

// บรรทัดรายละเอียดของการไม่อยู่แต่ละประเภท (ช่วงวัน/เวลา · สถานที่ · เหตุผล)
const AbsenceDetail = ({ t }) => (
  <div className="text-muted fs-sm">
    <Badge bg={TYPE_VARIANT[t.absence_type] || 'secondary'} className="me-1">{t.type_label}</Badge>
    {t.absence_type === 'offsite'
      ? <>{t.time_out} - {t.time_return} น.</>
      : <>{fmtDateTH(t.start_date)} - {fmtDateTH(t.end_date)}{t.days != null && <> ({fmtDays(t.days)} วัน)</>}</>}
    {t.location && <> · <i className="ri-map-pin-line" /> {t.location}</>}
    {t.reason && <> · {t.reason}</>}
  </div>
)

// การ์ดคาบสอนที่ได้รับผลกระทบ 1 คาบ สำหรับมุมมองมือถือ
const LeaveSlotCard = ({ s }) => (
  <div className="border rounded p-3">
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">คาบ {s.period_order} · {s.c_class ? `${s.c_class}/${s.r_no}` : '-'}</div>
      <span className="fs-xs text-muted">{formatTimeShort(s.period_begin)} - {formatTimeShort(s.period_end)}</span>
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <code className="me-1">{s.sub_subid}</code>{s.sub_subname}
    </div>
    <div className="mt-2 pt-2 border-top">
      {s.sub_id ? (
        <span className="text-success">
          <i className="ri-check-line me-1" />
          <b>{s.sub_teacher_name}</b>
          {s.sub_teacher_phone && <span className="text-muted ms-1 fs-xs">({s.sub_teacher_phone})</span>}
        </span>
      ) : (
        <Badge bg="danger">ยังไม่จัดสอนแทน</Badge>
      )}
    </div>
  </div>
)

// รายงานอ่านอย่างเดียวสำหรับหัวหน้าวิชาการสามัญ — ดูสถานะครูลา/การจัดสอนแทน
// การมอบหมายครูสอนแทนทำที่หน้า "จัดครูสอนแทน" ของฝ่ายการเรียนการสอน (SubstituteArrangement)
export default function TeachersOnLeave() {
  const { posIds = [] } = useAuth() || {}
  // ฝ่ายปกครอง (16) เข้าหน้านี้ได้ แต่เข้าหน้าภาพรวมสอนแทนไม่ได้
  const canSeeSubstituteOverview = posIds.some(id => SUBSTITUTE_OVERVIEW_POSITIONS.includes(id))
  const [date, setDate] = useState(todayLocal())
  const [teachers, setTeachers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expanded, setExpanded] = useState({}) // lr_id -> bool
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/hr/reports/leave-on-date?date=${date}`)
      if (res.data.success) {
        setTeachers(res.data.data.teachers)
        // เปิดแถวย่อยของทุกคนไว้ก่อนเมื่อโหลดวันใหม่
        setExpanded(Object.fromEntries(res.data.data.teachers.map(t => [t.key, true])))
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [date])

  useEffect(() => { fetchData() }, [fetchData])

  const dayName = getDayNameTHByIndex(new Date(date).getDay())
  const totalSlots = teachers.reduce((sum, t) => sum + t.schedule.length, 0)
  const coveredSlots = teachers.reduce((sum, t) => sum + t.schedule.filter(s => s.sub_id).length, 0)
  const countBy = (type) => teachers.filter(t => t.absence_type === type).length
  const typeBreakdown = [
    ['leave', 'ลา'], ['offsite', 'ออกนอกสถานที่'], ['training', 'อบรม/ประชุม/กิจกรรม'],
  ].filter(([type]) => countBy(type) > 0)
    .map(([type, label]) => `${label} ${countBy(type)}`).join(' · ')

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-user-unfollow-line me-2 text-primary" />
              ครูไม่อยู่ / การจัดสอนแทน
            </h4>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                size="sm"
                type="date"
                style={{ width: 170 }}
                value={date}
                onChange={e => e.target.value && setDate(e.target.value)}
              />
              <Button size="sm" variant="outline-secondary" onClick={() => setDate(todayLocal())}>
                วันนี้
              </Button>
              {canSeeSubstituteOverview && (
                <Button as={Link} to="/academic/substitute-overview" size="sm" variant="outline-primary">
                  <i className="ri-swap-line me-1" />ภาพรวมสอนแทน
                </Button>
              )}
              <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                  onClick={() => setView('table')} title="มุมมองตาราง">
                  <i className="ri-table-line" />
                </button>
                <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                  onClick={() => setView('card')} title="มุมมองการ์ด">
                  <i className="ri-layout-grid-line" />
                </button>
              </div>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <>
              <Alert variant={teachers.length === 0 ? 'success' : (coveredSlots === totalSlots ? 'success' : 'warning')} className="py-2">
                <b>วัน{dayName}ที่ {fmtDateTH(date)}</b> —{' '}
                {teachers.length === 0
                  ? 'ไม่มีครูลา / ไม่อยู่ในวันนี้'
                  : <>ครูไม่อยู่ {teachers.length} รายการ ({typeBreakdown}) · คาบสอนที่ได้รับผลกระทบ {totalSlots} คาบ · จัดสอนแทนแล้ว{' '}
                      <b>{coveredSlots}/{totalSlots}</b> คาบ
                      {totalSlots > coveredSlots && <> · <span className="text-danger fw-bold">ยังไม่จัด {totalSlots - coveredSlots} คาบ</span></>}
                    </>}
              </Alert>

              {teachers.map(t => {
                const covered = t.schedule.filter(s => s.sub_id).length
                const isOpen = !!expanded[t.key]
                return (
                  <Card className="card-one mb-3" key={t.key}>
                    <Card.Header
                      className="d-flex flex-wrap align-items-center gap-2"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setExpanded(ex => ({ ...ex, [t.key]: !ex[t.key] }))}
                    >
                      <i className={`ri-arrow-${isOpen ? 'down' : 'right'}-s-line fs-5`} />
                      <div>
                        <b>{t.t_name}</b>
                        {t.t_phone && <span className="text-muted fs-sm ms-2"><i className="ri-phone-line" /> {t.t_phone}</span>}
                        <AbsenceDetail t={t} />
                      </div>
                      <div className="ms-auto">
                        {t.schedule.length === 0 ? (
                          <Badge bg="secondary">ไม่มีคาบสอนวันนี้</Badge>
                        ) : covered === t.schedule.length ? (
                          <Badge bg="success"><i className="ri-check-double-line me-1" />จัดสอนแทนครบ {covered} คาบ</Badge>
                        ) : (
                          <Badge bg="danger">ยังไม่จัด {t.schedule.length - covered}/{t.schedule.length} คาบ</Badge>
                        )}
                      </div>
                    </Card.Header>
                    {isOpen && t.schedule.length > 0 && (
                      <Card.Body className="p-0">
                        {view === 'card' ? (
                          <div className="p-3">
                            <Row className="g-3">
                              {t.schedule.map((s, i) => (
                                <Col key={`${s.teach_id}-${s.period_id}-${i}`} xs={12} md={6} xl={4}>
                                  <LeaveSlotCard s={s} />
                                </Col>
                              ))}
                            </Row>
                          </div>
                        ) : (
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: 130 }}>คาบ</th>
                              <th>วิชา</th>
                              <th style={{ width: 120 }}>ห้องเรียน</th>
                              <th style={{ width: 240 }}>การสอนแทน</th>
                            </tr>
                          </thead>
                          <tbody>
                            {t.schedule.map((s, i) => (
                              <tr key={`${s.teach_id}-${s.period_id}-${i}`}>
                                <td>
                                  คาบ {s.period_order}
                                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    {formatTimeShort(s.period_begin)} - {formatTimeShort(s.period_end)}
                                  </div>
                                </td>
                                <td>
                                  <code className="me-1">{s.sub_subid}</code>{s.sub_subname}
                                </td>
                                <td>{s.c_class ? `${s.c_class}/${s.r_no}` : '-'}</td>
                                <td>
                                  {s.sub_id ? (
                                    <span className="text-success">
                                      <i className="ri-check-line me-1" />
                                      <b>{s.sub_teacher_name}</b>
                                      {s.sub_teacher_phone && (
                                        <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>({s.sub_teacher_phone})</span>
                                      )}
                                    </span>
                                  ) : (
                                    <Badge bg="danger">ยังไม่จัดสอนแทน</Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        )}
                      </Card.Body>
                    )}
                  </Card>
                )
              })}
            </>
          )}
        </Container>
      </div>
      <Footer />
    </>
  )
}
